import { computed, Service, signal } from '@angular/core';
import { EmailValidationResult } from '../models/email-validation-result.model';

/** What `sign-in-redirector` stores for the rest of the tab's session. */
interface SignInContext {
  returnUrl: string;
  correlationID?: string;
}

/**
 * State shared by the sign-in steps, forgot-password and registration. The return URL and
 * correlation ID come from sessionStorage, loaded once at startup; the rest is entered on the way
 * through sign-in and lives in memory only, passwords included.
 */
@Service()
export class SignInService {
  private readonly contextKey = 'Citadel.Auth.Service.SignInContext';

  // The authorize URL IdentityServer sent the user here with, and what the app put inside it.
  readonly returnUrl = signal('');
  readonly correlationID = signal('');
  readonly clientId = computed(() => matchFromUrl(this.returnUrl(), 'client_id'));
  readonly impersonate = computed(() => matchFromUrl(this.returnUrl(), 'impersonate'));
  readonly authRedirect = computed(() => paramFromUrl(this.returnUrl(), 'authRedirect'));

  // Entered on sign-in's steps.
  readonly email = signal('');
  readonly account = signal<EmailValidationResult | undefined>(undefined);
  readonly password = signal('');
  // The channel the OTP was sent by, and whether one has been sent for this account.
  readonly otpType = signal<string | undefined>(undefined);
  readonly otpSent = signal(false);

  // From the forgot-password request on sign-in's password step.
  readonly passwordResetEmail = signal('');
  readonly passwordResetRequestId = signal('');
  otpResetRequestId?: string;

  /** Forgets everything entered, for a fresh start on sign-in's first step. */
  resetEntries() {
    this.email.set('');
    this.account.set(undefined);
    this.password.set('');
    this.otpType.set(undefined);
    this.otpSent.set(false);
    this.passwordResetEmail.set('');
    this.passwordResetRequestId.set('');
    this.otpResetRequestId = undefined;
  }

  /** Reads the context `storeContext` saved, if any. */
  loadContext() {
    let context: Partial<SignInContext> | null = null;
    try {
      context = JSON.parse(sessionStorage.getItem(this.contextKey) ?? 'null');
    } catch {
    }
    this.returnUrl.set(typeof context?.returnUrl === 'string' ? context.returnUrl : '');
    this.correlationID.set(typeof context?.correlationID === 'string' ? context.correlationID : '');
  }

  /** Saves the return URL and the correlation ID inside it, for later pages and refreshes. */
  storeContext(returnUrl: string) {
    const context: SignInContext = { returnUrl, correlationID: paramFromUrl(returnUrl, 'correlationID') };
    try {
      sessionStorage.setItem(this.contextKey, JSON.stringify(context));
    } catch {
    }
    this.returnUrl.set(context.returnUrl);
    this.correlationID.set(context.correlationID ?? '');
  }

  clearContext() {
    try {
      sessionStorage.removeItem(this.contextKey);
    } catch {
    }
    this.returnUrl.set('');
    this.correlationID.set('');
  }
}

/**
 * The value of `name` in `url`, limited to letters, digits, `_`, `.` and `-`. Kept for `client_id`
 * and `impersonate` as sign-in always read them: `impersonate` goes unencoded into the Azure
 * challenge URL, so it must not be able to carry `&` or `=`.
 */
function matchFromUrl(url: string, name: string): string {
  const match = RegExp(`${name}=([\\.\\w-]+)`).exec(url);
  return match?.[1] ?? '';
}

/** The decoded value of query parameter `name` in `url`. */
function paramFromUrl(url: string, name: string): string {
  const query = url.indexOf('?');
  return query === -1 ? '' : new URLSearchParams(url.slice(query + 1)).get(name) ?? '';
}
